import React, { useState, useEffect, useRef } from 'react';
import { ShoppingCart, X, RefreshCw, Ban } from 'lucide-react';
import Layout from '../components/Layout';
import { integracoesAPI } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import { useConfirm } from '../hooks/useConfirm';
import { toast } from 'react-toastify';
import PedidosSkeleton from '../components/skeletons/PedidosSkeleton';

const colunas = [
  { key: 'pendente', label: '🔔 Novo', color: 'border-t-yellow-400', dropBg: 'ring-yellow-400', badge: 'bg-yellow-100 text-yellow-700' },
  { key: 'preparando', label: '📦 Preparando', color: 'border-t-blue-400', dropBg: 'ring-blue-400', badge: 'bg-blue-100 text-blue-700' },
  { key: 'saiu_entrega', label: '🚀 A caminho', color: 'border-t-orange-400', dropBg: 'ring-orange-400', badge: 'bg-orange-100 text-orange-700' },
  { key: 'entregue', label: '✅ Concluído', color: 'border-t-green-400', dropBg: 'ring-green-400', badge: 'bg-green-100 text-green-700' },
];

const Pedidos = () => {
  const { darkMode } = useTheme();
  const showConfirm = useConfirm();
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [editItens, setEditItens] = useState([]);
  const [buscaProduto, setBuscaProduto] = useState('');
  const [sugestoes, setSugestoes] = useState([]);
  const [filtroData, setFiltroData] = useState('hoje');
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const prevCountRef = useRef(-1);

  // Skeleton só na primeira carga. Não dá para inferir isso de `pedidos.length`:
  // numa conta sem pedidos, o refresh manual (que religa `loading`) apagaria a
  // tela inteira. Daí o ref, marcado quando a primeira resposta chega.
  const jaCarregouRef = useRef(false);
  const primeiraCarga = loading && !jaCarregouRef.current;

  const loadPedidos = async () => {
    try {
      const res = await integracoesAPI.listar();
      const novos = res.data || [];
      // Notificação sonora se tem pedido novo pendente
      const pendenteNovo = novos.filter(p => p.status === 'pendente').length;
      if (prevCountRef.current >= 0 && pendenteNovo > prevCountRef.current) {
        try { new Audio('/pedido-notification.mp3').play().catch(() => {}); } catch {}
        toast.info('🔔 Novo pedido recebido!');
      }
      prevCountRef.current = pendenteNovo;
      setPedidos(novos); return novos;
    }
    catch { toast.error('Erro ao carregar pedidos'); }
    finally { jaCarregouRef.current = true; setLoading(false); }
  };

  useEffect(() => {
    loadPedidos().then(novos => {
      const params = new URLSearchParams(window.location.search);
      const pedidoId = params.get('pedido');
      if (pedidoId && novos) {
        const p = novos.find(x => x.id === pedidoId);
        if (p) abrirModal(p);
      }
    });
    const i = setInterval(loadPedidos, 30000);
    return () => clearInterval(i);
  }, []);

  // Abrir modal com itens editáveis
  const abrirModal = (p) => {
    setModal(p);
    setEditItens((p.itens || []).map(i => ({ ...i })));
    setBuscaProduto('');
    setSugestoes([]);
  };

  const buscarProdutoAutocomplete = async (q) => {
    setBuscaProduto(q);
    if (q.length < 2) { setSugestoes([]); return; }
    try {
      const res = await integracoesAPI.buscarProduto(q);
      setSugestoes(res.data || []);
    } catch { setSugestoes([]); }
  };

  const adicionarItem = (produto) => {
    setEditItens(prev => {
      const existe = prev.find(i => i.produto_externo_id === produto.produto_externo_id);
      if (existe) return prev.map(i => i.produto_externo_id === produto.produto_externo_id ? { ...i, qtd: i.qtd + 1 } : i);
      return [...prev, { nome: produto.nome, qtd: 1, preco: parseFloat(produto.preco), produto_externo_id: produto.produto_externo_id }];
    });
    setBuscaProduto('');
    setSugestoes([]);
  };

  const removerItem = (idx) => setEditItens(prev => prev.filter((_, i) => i !== idx));
  const alterarQtd = (idx, qtd) => setEditItens(prev => prev.map((item, i) => i === idx ? { ...item, qtd: Math.max(1, qtd) } : item));

  const salvarEdicao = async () => {
    const total = editItens.reduce((s, i) => s + i.preco * i.qtd, 0);
    try {
      await integracoesAPI.editarPedido(modal.id, { itens: editItens, total, delivery: modal.delivery, endereco: modal.endereco, pagamento: modal.pagamento });
      const atualizado = { ...modal, itens: editItens, total };
      setModal(atualizado);
      setPedidos(prev => prev.map(p => p.id === modal.id ? atualizado : p));
      toast.success('Pedido atualizado e cliente notificado');
    } catch { toast.error('Erro ao atualizar'); }
  };

  const atualizarStatus = async (pedidoId, status) => {
    try {
      await integracoesAPI.atualizarStatusPedido(pedidoId, status);
      setPedidos(prev => prev.map(p => p.id === pedidoId ? { ...p, status } : p));
      if (modal?.id === pedidoId) setModal(m => ({ ...m, status }));
    } catch { toast.error('Erro ao atualizar'); }
  };

  const cancelar = async (pedidoId) => {
    try {
      await integracoesAPI.atualizarStatusPedido(pedidoId, 'cancelado');
      setPedidos(prev => prev.map(p => p.id === pedidoId ? { ...p, status: 'cancelado' } : p));
      setModal(null);
      toast.success('Pedido cancelado');
    } catch { toast.error('Erro ao cancelar'); }
  };

  const deletar = async (pedidoId) => {
    try {
      await integracoesAPI.deletarPedido(pedidoId);
      setPedidos(prev => prev.filter(p => p.id !== pedidoId));
      setModal(null);
      toast.success('Pedido removido');
    } catch { toast.error('Erro ao remover'); }
  };

  // Drag handlers
  const onDragStart = (e, pedido) => { setDragging(pedido); e.dataTransfer.effectAllowed = 'move'; };
  const onDragEnd = () => { setDragging(null); setDragOver(null); };
  const onDragOverCol = (e, colKey) => { e.preventDefault(); setDragOver(colKey); };
  const onDragLeaveCol = () => setDragOver(null);
  const onDrop = (e, colKey) => {
    e.preventDefault();
    setDragOver(null);
    if (dragging && dragging.status !== colKey) {
      atualizarStatus(dragging.id, colKey);
    }
    setDragging(null);
  };

  const ativos = pedidos.filter(p => p.status !== 'cancelado');
  const cancelados = pedidos.filter(p => p.status === 'cancelado');

  // Filtro por data
  const agora = new Date();
  const filtrarPorData = (p) => {
    if (filtroData === 'todos') return true;
    if (!p.created_at) return false;
    const d = new Date(p.created_at);
    if (filtroData === 'hoje') return d.toDateString() === agora.toDateString();
    if (filtroData === '7dias') return (agora - d) < 7 * 86400000;
    if (filtroData === '30dias') return (agora - d) < 30 * 86400000;
    return true;
  };
  const pedidosFiltrados = pedidos.filter(filtrarPorData);
  const ativosFiltrados = pedidosFiltrados.filter(p => p.status !== 'cancelado');
  const canceladosFiltrados = pedidosFiltrados.filter(p => p.status === 'cancelado');

  const card = darkMode ? 'bg-zinc-800' : 'bg-white';
  const t1 = darkMode ? 'text-white' : 'text-gray-900';
  const t2 = darkMode ? 'text-gray-400' : 'text-gray-500';

  // Analytics
  
  const pedidosHoje = pedidosFiltrados;
  const faturamentoHoje = pedidosFiltrados.filter(p => p.status !== 'cancelado').reduce((s, p) => s + parseFloat(p.total || 0), 0);
  const ticketMedio = pedidosFiltrados.filter(p => p.status !== "cancelado").length > 0
    ? faturamentoHoje / pedidosFiltrados.filter(p => p.status !== 'cancelado').length : 0;

  return (
    <Layout fullWidth>
      <div className="p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <h1 className={`text-xl font-bold flex items-center gap-2 ${t1}`}>
            <ShoppingCart size={22} /> Pedidos
          </h1>
          <div className="flex items-center gap-3">
            <div className={`flex flex-wrap rounded-lg overflow-hidden border ${darkMode ? 'border-zinc-700' : 'border-gray-200'}`}>
              {['hoje', '7dias', '30dias', 'todos'].map(f => (
                <button key={f} onClick={() => setFiltroData(f)}
                  className={`px-4 py-1.5 text-xs font-medium transition ${filtroData === f
                    ? 'bg-blue-500 text-white'
                    : darkMode ? 'bg-zinc-800 text-gray-400 hover:bg-zinc-700' : 'bg-white text-gray-500 hover:bg-gray-50'
                  }`}>
                  {f === 'hoje' ? 'Hoje' : f === '7dias' ? '7 dias' : f === '30dias' ? '30 dias' : 'Todos'}
                </button>
              ))}
            </div>
            <button onClick={() => { setLoading(true); loadPedidos(); }} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'}`}>
              <RefreshCw size={16} className={`${t2} ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {primeiraCarga ? (
          <PedidosSkeleton />
        ) : (
          <>
          {/* Analytics */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mb-4">
            <div className={`rounded-lg p-3 border ${darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'}`}>
              <p className={`text-[10px] uppercase font-semibold ${t2}`}>Pedidos</p>
              <p className={`text-2xl font-bold ${t1}`}>{pedidosFiltrados.length}</p>
            </div>
            <div className={`rounded-lg p-3 border ${darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'}`}>
              <p className={`text-[10px] uppercase font-semibold ${t2}`}>Faturamento</p>
              <p className="text-2xl font-bold text-green-500">R$ {faturamentoHoje.toFixed(2)}</p>
            </div>
            <div className={`rounded-lg p-3 border ${darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'}`}>
              <p className={`text-[10px] uppercase font-semibold ${t2}`}>Pendentes</p>
              <p className="text-2xl font-bold text-yellow-500">{ativosFiltrados.filter(p => p.status === 'pendente').length}</p>
            </div>
            <div className={`rounded-lg p-3 border ${darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'}`}>
              <p className={`text-[10px] uppercase font-semibold ${t2}`}>Concluídos</p>
              <p className="text-2xl font-bold text-green-500">{pedidosFiltrados.filter(p => p.status === 'entregue').length}</p>
            </div>
            <div className={`rounded-lg p-3 border ${darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'}`}>
              <p className={`text-[10px] uppercase font-semibold ${t2}`}>Delivery</p>
              <p className="text-2xl font-bold text-orange-500">{pedidosFiltrados.filter(p => p.delivery).length}</p>
            </div>
            <div className={`rounded-lg p-3 border ${darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'}`}>
              <p className={`text-[10px] uppercase font-semibold ${t2}`}>Ticket médio</p>
              <p className={`text-2xl font-bold ${t1}`}>R$ {ticketMedio.toFixed(2)}</p>
            </div>
          </div>

          {/* Kanban */}
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 min-h-0">
            {colunas.map(col => {
              const items = ativosFiltrados.filter(p => p.status === col.key);
              const isOver = dragOver === col.key;
              return (
                <div key={col.key}
                  onDragOver={e => onDragOverCol(e, col.key)}
                  onDragLeave={onDragLeaveCol}
                  onDrop={e => onDrop(e, col.key)}
                  className={`flex flex-col rounded-xl border-t-4 ${col.color} transition-all ${
                    isOver ? `ring-2 ${col.dropBg}` : ''
                  } ${darkMode ? 'bg-zinc-800/50 border-x border-b border-zinc-700' : 'bg-gray-50 border-x border-b border-gray-200'}`}>
                
                  <div className="p-3 pb-2">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-semibold ${t1}`}>{col.label}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${col.badge}`}>{items.length}</span>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-2 pt-0 space-y-2">
                    {items.map(p => (
                      <div key={p.id} draggable onDragStart={e => onDragStart(e, p)} onDragEnd={onDragEnd}
                        onClick={() => abrirModal(p)}
                        className={`rounded-lg p-3 cursor-grab active:cursor-grabbing border transition hover:shadow-md ${
                          dragging?.id === p.id ? 'opacity-40' : ''
                        } ${card} ${darkMode ? 'border-zinc-700' : 'border-gray-200'}`}>
                        <div className="flex justify-between items-start mb-2">
                          <span className={`text-base font-bold ${t1}`}>R$ {parseFloat(p.total || 0).toFixed(2)}</span>
                          <span className={`text-[10px] ${t2}`}>{p.created_at ? `${new Date(p.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}${filtroData !== 'hoje' ? ` · ${new Date(p.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}` : ''}` : ''}</span>
                        </div>
                        <div className={`text-xs space-y-0.5 mb-2 ${t2}`}>
                          {(p.itens || []).slice(0, 3).map((item, j) => (
                            <p key={j} className="truncate">{item.qtd}x {item.nome}</p>
                          ))}
                          {(p.itens || []).length > 3 && <p className="italic">+{p.itens.length - 3} itens</p>}
                        </div>
                        <div className={`flex items-center justify-between text-[10px] pt-1 border-t ${darkMode ? 'border-zinc-700' : 'border-gray-100'} ${t2}`}>
                          <span>{p.nome_contato || p.telefone}</span>
                          <div className="flex items-center gap-1">
                            {p.delivery ? <span>🛵 Delivery</span> : <span>🏪 Retirada</span>}
                            {p.pagamento && <span>· {p.pagamento}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                    {items.length === 0 && (
                      <div className={`text-center py-12 ${t2}`}>
                        <p className="text-2xl mb-1">📭</p>
                        <p className="text-xs">Nenhum pedido</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          </>
        )}
      </div>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setModal(null)}>
          <div className={`rounded-xl p-0 w-full max-w-[700px] max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-zinc-800 text-white' : 'bg-white text-gray-900'}`} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-2 px-6 pt-6">
              <h3 className="text-xl font-bold">Pedido</h3>
              <button onClick={() => setModal(null)} className="p-1 hover:opacity-70"><X size={20} /></button>
            </div>

            <div className="flex items-center gap-3 px-6 mb-1">
              <span className={`text-sm px-3 py-1 rounded-full font-medium ${(colunas.find(c => c.key === modal.status) || { badge: 'bg-red-100 text-red-700' }).badge}`}>
                {modal.status === 'cancelado' ? '❌ Cancelado' : (colunas.find(c => c.key === modal.status) || colunas[0]).label}
              </span>
            </div>
            <div className={`flex items-center gap-4 text-xs px-6 mb-4 ${t2}`}>
              <span>📞 {modal.telefone}</span>
              {modal.nome_contato && <span>👤 {modal.nome_contato}</span>}
              <span>📅 {modal.created_at ? new Date(modal.created_at).toLocaleString('pt-BR') : ''}</span>
            </div>

            {modal.status !== 'cancelado' && (
              <div className="px-6 mb-4">
                <div className="flex items-center gap-1.5">
                  {colunas.map((col, i) => (
                    <React.Fragment key={col.key}>
                      {i > 0 && <span className={`text-[10px] ${t2}`}>→</span>}
                      <button onClick={() => atualizarStatus(modal.id, col.key)}
                        className={`flex-1 py-1.5 text-[11px] rounded-lg font-medium transition ${
                          modal.status === col.key
                            ? `${col.badge} ring-2 ring-current`
                            : `${darkMode ? 'bg-zinc-700 text-gray-400 hover:bg-zinc-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`
                        }`}>
                        {col.label}
                      </button>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}

            {modal.status === 'pendente' && (
              <div className="relative px-6 mb-4">
                <input value={buscaProduto} onChange={e => buscarProdutoAutocomplete(e.target.value)}
                  placeholder="🔍 Buscar produto pra adicionar..."
                  className={`w-full text-sm px-4 py-3 rounded-xl border ${darkMode ? 'bg-zinc-700 border-zinc-600 text-white placeholder-gray-400' : 'border-gray-200 placeholder-gray-400'}`} />
                {sugestoes.length > 0 && (
                  <div className={`absolute z-10 left-6 right-6 mt-1 rounded-xl border shadow-xl max-h-56 overflow-y-auto ${darkMode ? 'bg-zinc-800 border-zinc-600' : 'bg-white border-gray-200'}`}>
                    {sugestoes.map((s, i) => (
                      <button key={i} onClick={() => adicionarItem(s)}
                        className={`w-full text-left px-4 py-3 flex items-center gap-3 border-b last:border-0 transition ${darkMode ? 'text-gray-200 border-zinc-700 hover:bg-zinc-700' : 'text-gray-700 border-gray-100 hover:bg-blue-50'}`}>
                        {s.imagem_url ? <img src={s.imagem_url} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" /> : <div className={`w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center text-lg ${darkMode ? 'bg-zinc-600' : 'bg-gray-200'}`}>📦</div>}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{s.nome}</p>
                          <p className={`text-xs ${t2}`}>{s.categoria || ''}</p>
                        </div>
                        <span className="font-semibold text-sm whitespace-nowrap">R$ {parseFloat(s.preco).toFixed(2)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="px-6 pb-4">
            <div className={`rounded-xl p-5 mb-4 ${darkMode ? 'bg-zinc-700/50' : 'bg-gray-50'}`}>
              {modal.status === 'pendente' ? (
                <>
                  <p className={`text-sm font-semibold mb-3 ${t1}`}>Itens do pedido</p>
                  {editItens.length === 0 && <p className={`text-sm text-center py-6 ${t2}`}>Nenhum item adicionado</p>}
                  <div className="space-y-0">
                  {editItens.map((item, j) => (
                    <div key={j} className={`flex items-center gap-3 py-3 border-b last:border-0 ${darkMode ? 'border-zinc-600' : 'border-gray-200'}`}>
                      <div className={`w-12 h-12 rounded-lg flex-shrink-0 flex items-center justify-center text-xl ${darkMode ? 'bg-zinc-600' : 'bg-gray-200'}`}>📦</div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${t1}`}>{item.nome}</p>
                        <p className={`text-xs ${t2}`}>R$ {parseFloat(item.preco || 0).toFixed(2)} / un</p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => alterarQtd(j, item.qtd - 1)} className={`w-8 h-8 rounded-full flex items-center justify-center text-base font-bold transition ${darkMode ? 'bg-zinc-600 text-white hover:bg-zinc-500' : 'bg-gray-200 hover:bg-gray-300'}`}>−</button>
                        <span className={`w-8 text-center text-sm font-semibold ${t1}`}>{item.qtd}</span>
                        <button onClick={() => alterarQtd(j, item.qtd + 1)} className={`w-8 h-8 rounded-full flex items-center justify-center text-base font-bold transition ${darkMode ? 'bg-zinc-600 text-white hover:bg-zinc-500' : 'bg-gray-200 hover:bg-gray-300'}`}>+</button>
                      </div>
                      <span className={`text-sm font-semibold w-24 text-right ${t1}`}>R$ {(parseFloat(item.preco || 0) * item.qtd).toFixed(2)}</span>
                      <button onClick={() => removerItem(j)} className="text-red-400 hover:text-red-500 p-1.5 rounded-full hover:bg-red-50 transition">✕</button>
                    </div>
                  ))}
                  </div>
                  <div className={`border-t mt-3 pt-4 flex justify-between text-lg font-bold ${darkMode ? 'border-zinc-600' : 'border-gray-200'}`}>
                    <span>Total</span>
                    <span className="text-green-500">R$ {editItens.reduce((s, i) => s + parseFloat(i.preco || 0) * i.qtd, 0).toFixed(2)}</span>
                  </div>
                </>
              ) : (
                <>
                  {(modal.itens || []).map((item, j) => (
                    <div key={j} className={`flex items-center gap-3 py-3 border-b last:border-0 ${darkMode ? 'border-zinc-600' : 'border-gray-200'}`}>
                      <div className={`w-12 h-12 rounded-lg flex-shrink-0 flex items-center justify-center text-xl ${darkMode ? 'bg-zinc-600' : 'bg-gray-200'}`}>📦</div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${t1}`}>{item.nome}</p>
                        <p className={`text-xs ${t2}`}>R$ {parseFloat(item.preco || 0).toFixed(2)} × {item.qtd}</p>
                      </div>
                      <span className={`text-sm font-semibold ${t1}`}>R$ {(parseFloat(item.preco || 0) * item.qtd).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className={`border-t mt-3 pt-4 flex justify-between text-lg font-bold ${darkMode ? 'border-zinc-600' : 'border-gray-200'}`}>
                    <span>Total</span>
                    <span className="text-green-500">R$ {parseFloat(modal.total || 0).toFixed(2)}</span>
                  </div>
                </>
              )}
            </div>
            </div>

            <div className={`space-y-2 text-sm mb-4 px-6 ${t2}`}>
              {modal.status === 'pendente' ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <label className="text-xs w-20">Entrega:</label>
                    <select value={modal.delivery ? 'delivery' : 'retirada'}
                      onChange={e => setModal(m => ({ ...m, delivery: e.target.value === 'delivery' }))}
                      className={`text-xs px-2 py-1 rounded border ${darkMode ? 'bg-zinc-700 border-zinc-600 text-white' : 'border-gray-300'}`}>
                      <option value="delivery">🛵 Delivery</option>
                      <option value="retirada">🏪 Retirada</option>
                    </select>
                  </div>
                  {modal.delivery && (
                    <div className="flex items-center gap-2">
                      <label className="text-xs w-20">Endereço:</label>
                      <input value={modal.endereco || ''} onChange={e => setModal(m => ({ ...m, endereco: e.target.value }))}
                        className={`flex-1 text-xs px-2 py-1 rounded border ${darkMode ? 'bg-zinc-700 border-zinc-600 text-white' : 'border-gray-300'}`} />
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <label className="text-xs w-20">Pagamento:</label>
                    <select value={modal.pagamento || ''} onChange={e => setModal(m => ({ ...m, pagamento: e.target.value }))}
                      className={`text-xs px-2 py-1 rounded border ${darkMode ? 'bg-zinc-700 border-zinc-600 text-white' : 'border-gray-300'}`}>
                      <option value="cartao">Cartão</option>
                      <option value="pix">PIX</option>
                      <option value="dinheiro">Dinheiro</option>
                    </select>
                  </div>
                  </div>
              ) : (
                <>
                  {modal.delivery ? <p>🛵 Entrega: {modal.endereco || 'Não informado'}</p> : <p>🏪 Retirada na loja</p>}
                  {modal.pagamento && <p>💳 Pagamento: {modal.pagamento}</p>}
                </>
              )}
            </div>

            {modal.status !== 'cancelado' && (
              <div className={`border-t pt-4 px-6 pb-6 ${darkMode ? 'border-zinc-600' : 'border-gray-200'}`}>
                <div className="flex gap-3">
                  <button onClick={() => showConfirm({ title: 'Cancelar pedido', message: 'Tem certeza que deseja cancelar este pedido? O cliente será notificado.', onConfirm: () => cancelar(modal.id) })}
                    className="flex-1 py-2.5 text-sm text-red-500 border border-red-200 rounded-lg hover:bg-red-50 flex items-center justify-center gap-1 font-medium">
                    <Ban size={14} /> Cancelar pedido
                  </button>
                  {modal.status === 'pendente' && (
                    <button onClick={salvarEdicao} className="flex-1 gradient-button py-2.5 text-sm text-white rounded-lg font-medium">Salvar alterações</button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Pedidos;
